import React, { useCallback } from 'react';

import { Box, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';

import { useDataBinding } from '../../hooks/useDataBinding.tsx';
import { useDynamicString } from '../../hooks/useDynamicValue.tsx';
import { useValidation } from '../../hooks/useValidation.tsx';
import { isDataBinding } from '../../types/A2UICommon.types.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type { A2UITextFieldProps, TextFieldVariant } from '../../types/A2UIComponents.types.tsx';

const variantToType: Record<TextFieldVariant, string> = {
	shortText: 'text',
	longText: 'text',
	number: 'number',
	obscured: 'password',
};

type Props = {
	definition: A2UIComponentDefinition;
};

export const A2UITextField = ({ definition }: Props): React.JSX.Element => {
	const def = definition as A2UITextFieldProps;
	const label = useDynamicString(def.label);
	const legacyVariant = (def as unknown as { type?: string }).type;
	const variant: TextFieldVariant =
		def.variant ??
		(legacyVariant && legacyVariant in variantToType
			? (legacyVariant as TextFieldVariant)
			: undefined) ??
		'shortText';
	const { isInvalid, errorMessages } = useValidation(def.checks);
	const valueSource = def.value ?? (def as unknown as { text?: A2UITextFieldProps['value'] }).text;

	// Two-way binding: if value is a DataBinding, use useDataBinding
	const bindingPath = isDataBinding(valueSource) ? valueSource.path : null;
	const [boundValue, setBoundValue] = useDataBinding<string>(bindingPath ?? '/___noop');
	const staticValue = useDynamicString(valueSource);

	const value = bindingPath ? (boundValue ?? '') : staticValue;

	const handleChange = useCallback(
		(e: React.FormEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
			if (bindingPath) {
				setBoundValue(e.currentTarget.value);
			}
		},
		[bindingPath, setBoundValue],
	);

	const fieldId = `a2ui-field-${def.id}`;

	// Long text → Textarea
	if (variant === 'longText') {
		return (
			<Box>
				<label htmlFor={fieldId}>
					<Text as="span" weight="semibold">
						{label}
					</Text>
				</label>
				<TextArea
					id={fieldId}
					value={value}
					onChange={handleChange}
					isInvalid={isInvalid}
					aria-label={def.accessibility?.label ?? label}
				/>
				{isInvalid &&
					errorMessages.map((msg, i) => (
						<Text key={i} as="span" size="small">
							{msg}
						</Text>
					))}
			</Box>
		);
	}

	// Short text, number, obscured → Textfield
	return (
		<Box>
			<label htmlFor={fieldId}>
				<Text as="span" weight="semibold">
					{label}
				</Text>
			</label>
			<Textfield
				id={fieldId}
				type={variantToType[variant]}
				value={value}
				onChange={handleChange}
				isInvalid={isInvalid}
				aria-label={def.accessibility?.label ?? label}
			/>
			{isInvalid &&
				errorMessages.map((msg, i) => (
					<Text key={i} as="span" size="small">
						{msg}
					</Text>
				))}
		</Box>
	);
};
